import { useCallback, useEffect, useRef, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Box, FormControl, FormHelperText, FormLabel } from '@mui/material'

import { DefaultElementProps } from 'common/interfaces'
import { useBoolean, useIsOnline } from 'utils/hook'
import { getFileExtensionAndUniqName, S3Bucket, useUploadFileToAWS } from 'utils/uploadFileToS3'
import validationsToMapRules from 'utils/validationsToRules'

import AudioLoading from './components/AudioLoading'
import AudioPlayer from './components/AudioPlayer'
import RecordingBox from './components/RecordingBox'
import StartRecordBox from './components/StartRecordBox'
import { AudioRecord as AudioRecordProps } from './interface'

const maxAudioTimeSec = 900 // 15 min

export default function AudioRecord({
  label,
  startText = 'Tap to record. You can choose to stop at any time.',
  recordingText = 'Recording in progress',
  name,
  validations,
}: AudioRecordProps & DefaultElementProps): JSX.Element {
  const isOnline = useIsOnline()
  const { mutate: sendFileToAWS } = useUploadFileToAWS()

  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: validationsToMapRules(validations),
  })

  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null)
  const mediaRecorder = useRef<MediaRecorder>()

  const [isLoading, setStartLoading, setStopLoading] = useBoolean(false)
  const [isRecording, setStartRecording, setStopRecording] = useBoolean(false)
  const [isPlaying, setStartPlaying, setStopPlaying] = useBoolean(false)

  const [audio, setAudio] = useState<string | undefined>(field.value?.audio ?? undefined)

  const [duration, setDuration] = useState(field.value?.duration ?? 0)
  const [trackProgress, setTrackProgress] = useState(0)

  const startRecording = async (): Promise<void> => {
    if ('MediaRecorder' in window) {
      try {
        setStartRecording()

        const streamData = await navigator.mediaDevices.getUserMedia({
          audio: true,
        })

        mediaRecorder.current = new MediaRecorder(streamData)

        mediaRecorder.current.ondataavailable = ({ data }) => {
          if (typeof data === 'undefined' || data.size === 0) {
            return
          }

          setStartLoading()

          const audioUrl = URL.createObjectURL(data)
          setAudioRef(new Audio(audioUrl))

          const extension = data.type.split('audio/')[1].split(';')[0]
          const { fileUniqName } = getFileExtensionAndUniqName(`audio.${extension}`)

          const awsParams = {
            Body: data,
            Bucket: process.env.AWS_BUCKET_NAME!,
            Key: `${process.env.AWS_FILE_PATH!}${fileUniqName}`,
          }

          sendFileToAWS(awsParams, {
            onSuccess: () => {
              setAudio(awsParams.Key)
              setStopLoading()
            },
            onError: (err) => {
              if (!isOnline) {
                setAudio(awsParams.Key)
                setStopLoading()

                return
              }

              setStopRecording()
              toast.error("Sorry, your audio didn't load")
              console.log(err.message)
            },
          })
        }

        mediaRecorder.current.start()
      } catch (err) {
        setStopRecording()
        toast.error(err.message)
      }
    } else {
      toast.error('The MediaRecorder API is not supported in your browser.')
    }
  }

  const stopRecording = (): void => {
    mediaRecorder.current!.stop()
    setStopRecording()
  }

  const playRecording = (): void => {
    if (duration === trackProgress) {
      setTrackProgress(0)
    }

    void audioRef!.play()
    setStartPlaying()
  }

  const pauseRecording = (): void => {
    audioRef!.pause()
    setStopPlaying()
  }

  const deleteRecording = (): void => {
    pauseRecording()
    setAudioRef(null)
    setAudio(undefined)
    setDuration(0)
    setTrackProgress(0)
    field.onChange(undefined)
  }

  const handleProgressChange = (e: Event, value: number): void => {
    const selectedTime = Math.round((value * duration) / 100)
    audioRef!.currentTime = selectedTime
    setTrackProgress(selectedTime)
  }

  const setAudioFromAWS = useCallback(async (): Promise<void> => {
    const src = await S3Bucket.getSignedUrlPromise('getObject', {
      Bucket: process.env.AWS_BUCKET_NAME!,
      Key: audio,
      Expires: 86400,
    })

    setAudioRef(new Audio(src))
    setStopLoading()
  }, [])

  useEffect(() => {
    let timeOut: ReturnType<typeof setTimeout>

    if (trackProgress >= duration) {
      setStopPlaying()
      return
    }

    if (isPlaying) {
      timeOut = setTimeout(() => {
        setTrackProgress((sec: number) => sec + 1)
      }, 1000)
    }

    return () => {
      clearTimeout(timeOut)
    }
  }, [trackProgress, isPlaying])

  useEffect(() => {
    let timeOut: ReturnType<typeof setTimeout>

    if (duration >= maxAudioTimeSec && mediaRecorder.current?.state === 'recording') {
      toast.error('Maximum time of record is 15 minutes, record is stop automatically')
      stopRecording()
      return
    }

    if (isRecording) {
      timeOut = setTimeout(() => {
        setDuration((sec: number) => sec + 1)
      }, 1000)
    }

    return () => {
      clearTimeout(timeOut)
    }
  }, [duration, isRecording])

  useEffect(() => {
    if (audio !== undefined) {
      field.onChange({ target: { value: { audio, duration } } })
    }
  }, [audio])

  useEffect(() => {
    if (audio !== undefined) {
      setStartLoading()
      void setAudioFromAWS()
    }
  }, [])

  const errorMessageId = `${name}-helper-text`

  return (
    <FormControl {...field} error={error !== undefined}>
      {label !== undefined && <FormLabel>{label}</FormLabel>}
      <Box
        sx={{ position: 'relative' }}
        className="audio-controls"
        aria-invalid={error !== undefined}
        aria-describedby={error !== undefined ? errorMessageId : undefined}
      >
        {isLoading && <AudioLoading />}
        {audio !== undefined && audioRef != null ? (
          <AudioPlayer
            audioSrc={audioRef.src}
            isPlaying={isPlaying}
            duration={duration}
            trackProgress={trackProgress}
            handleProgressChange={handleProgressChange}
            playRecording={playRecording}
            pauseRecording={pauseRecording}
            deleteRecording={deleteRecording}
          />
        ) : (
          <>
            {isRecording ? (
              <RecordingBox
                recordingText={recordingText}
                duration={duration}
                stopRecording={stopRecording}
              />
            ) : (
              <StartRecordBox startText={startText} startRecording={startRecording} />
            )}
          </>
        )}
      </Box>
      {error !== undefined && (
        <FormHelperText id={errorMessageId} role="alert">
          {error.message}
        </FormHelperText>
      )}
    </FormControl>
  )
}
